import { useEffect, useMemo, useState, Fragment } from "react";
import useWebTracking from "@/hooks/useWebTracking";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/constants";
import { Check, Lock } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import PlatformSupportChat from "@/components/PlatformSupportChat";
import useSiteContent from "@/hooks/useSiteContent";

const PRICING_DEFAULTS = {
    pricing_intro: {
        eyebrow: "Pricing",
        heading: "Every doctor deserves an authoritative digital identity.",
        subheading: "Choose a package that reflects the depth of your practice. Pay in two installments — 50% to begin, 50% before publish.",
    },
};

export default function Pricing() {
    useWebTracking();
    const [packages, setPackages] = useState([]);
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const { content } = useSiteContent(PRICING_DEFAULTS);
    const intro = content.pricing_intro || PRICING_DEFAULTS.pricing_intro;

    useEffect(() => {
        Promise.all([api.get("/packages"), api.get("/features")])
            .then(([p, f]) => {
                setPackages(p.data);
                setFeatures(f.data);
            })
            .finally(() => setLoading(false));
    }, []);

    const featuresByModule = useMemo(() => {
        const byMod = {};
        for (const f of features) {
            if (!byMod[f.module_code]) {
                byMod[f.module_code] = {
                    code: f.module_code,
                    name: f.module_name,
                    items: [],
                };
            }
            byMod[f.module_code].items.push(f);
        }
        return Object.values(byMod).sort((a, b) => a.code.localeCompare(b.code));
    }, [features]);

    return (
        <div className="min-h-screen bg-[#F5F8FA]">
            <MarketingNav />
            <section className="max-w-7xl mx-auto px-6 py-16">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold" data-testid="pricing-eyebrow">
                    {intro.eyebrow || "Pricing"}
                </div>
                <h1 className="mt-2 text-4xl lg:text-5xl font-bold tracking-tight text-[#123B5D] max-w-3xl" data-testid="pricing-heading">
                    {intro.heading}
                </h1>
                <p className="mt-3 text-[#17212B]/70 max-w-2xl" data-testid="pricing-sub">
                    {intro.subheading}
                </p>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pricing-grid">
                    {packages.map((p) => (
                        <div
                            key={p.code}
                            className={
                                "ddi-card p-6 ddi-lift " +
                                (p.code === "premium"
                                    ? "border-[#0F766E] shadow-lg ring-1 ring-[#0F766E]/30"
                                    : "")
                            }
                            data-testid={`pricing-card-${p.code}`}
                        >
                            {p.code === "premium" ? (
                                <span className="ddi-badge bg-teal-100 text-[#0F766E] mb-3">
                                    Most popular
                                </span>
                            ) : (
                                <span className="ddi-badge bg-[#123B5D]/5 text-[#123B5D] mb-3">
                                    Tier {p.tier_level}
                                </span>
                            )}
                            <div className="text-xl font-bold text-[#123B5D]">
                                {p.name}
                            </div>
                            <div className="text-xs text-[#17212B]/60 mt-1">
                                {p.tagline}
                            </div>
                            <div className="mt-4 text-3xl font-bold text-[#17212B]">
                                {inr(p.price_inr)}
                            </div>
                            <div className="text-xs text-[#17212B]/60 mt-1">
                                one-time · + {inr(p.amc_inr)} AMC / year
                            </div>
                            <div className="mt-4 text-sm text-[#17212B]/80 flex items-center gap-2">
                                <Check size={14} className="text-[#0F766E]" />
                                <span data-testid={`pricing-feature-count-${p.code}`}>{p.feature_count}</span> features included
                            </div>
                            <div className="mt-1 text-xs text-[#17212B]/60">
                                {p.advance_pct}% advance · balance before publish
                            </div>
                            <Link
                                to="/signup"
                                className="mt-6 block text-center ddi-teal-btn text-sm whitespace-nowrap px-3"
                                data-testid={`pricing-cta-${p.code}`}
                            >
                                {/* W2-UIUX-NEW-001 — "Get Legacy Platinum" wraps
                                    to 2 lines; shorten to "Get Platinum" to
                                    match other 4 single-line CTAs. */}
                                Get {p.name.replace(/^Legacy /, "")}
                            </Link>
                            <div className="mt-4 text-xs text-[#17212B]/50 leading-relaxed">
                                {p.description}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Feature matrix */}
                {!loading ? (
                    <div className="mt-16">
                        <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#123B5D]">
                            Every feature, every tier.
                        </h2>
                        <p className="mt-2 text-sm text-[#17212B]/70 max-w-2xl">
                            The list below is generated live from our database. Locked items are visible so you can plan an upgrade path.
                        </p>

                        <div className="mt-8 overflow-x-auto ddi-card">
                            <table className="w-full text-sm">
                                <thead className="bg-[#123B5D]/5 text-[#123B5D]">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">
                                            Module / Feature
                                        </th>
                                        {packages.map((p) => (
                                            <th
                                                key={p.code}
                                                className="text-center px-4 py-3 font-semibold whitespace-nowrap"
                                                data-testid={`matrix-col-${p.code}`}
                                            >
                                                {p.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {featuresByModule.map((mod) => (
                                        <Fragment key={"mod-" + mod.code}>
                                            <tr className="bg-[#F5F8FA]">
                                                <td
                                                    colSpan={packages.length + 1}
                                                    className="px-4 py-2 text-[11px] uppercase tracking-widest text-[#17212B]/60 font-semibold"
                                                >
                                                    Module {mod.code} · {mod.name}
                                                </td>
                                            </tr>
                                            {mod.items.map((f) => (
                                                <tr key={f.feature_code} className="border-t border-[#123B5D]/5">
                                                    <td className="px-4 py-2.5 text-[#17212B]/85">
                                                        <span className="font-mono text-[11px] text-[#17212B]/50 mr-2">
                                                            {f.feature_code}
                                                        </span>
                                                        {f.name}
                                                        {f.status === "coming_soon" ? (
                                                            <span className="ml-2 text-[10px] uppercase tracking-widest text-[#D97706]">
                                                                Coming soon
                                                            </span>
                                                        ) : null}
                                                    </td>
                                                    {packages.map((p) => {
                                                        const included = f.status === "active" && p.tier_level >= f.min_tier;
                                                        return (
                                                            <td
                                                                key={p.code}
                                                                className="px-4 py-2.5 text-center"
                                                                data-testid={`matrix-${p.code}-${f.feature_code}`}
                                                            >
                                                                {included ? (
                                                                    <Check size={14} className="inline text-[#0F766E]" />
                                                                ) : (
                                                                    <Lock size={12} className="inline text-[#17212B]/30" />
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="mt-16 text-center text-[#17212B]/50 text-sm">
                        Loading features…
                    </div>
                )}
            </section>
            <MarketingFooter />
            <PlatformSupportChat />
        </div>
    );
}
